import { clone, omit } from 'ramda'

import {
  ACCESS_LEVELS,
  ACCESS_LEVELS_ORDERED,
  ENDPOINTS,
} from '@/lib/consts.js'

import { useWebGateway } from '@booksy-com/vue-tools'
import { saveIosTabletIdentity, clearIosTabletIdentity } from '@/lib/ios-tablet-identity.js'

const HAD_IDENTITY_KEY = 'hadIdentity'
const INITIAL_IDENTITY = {
  accessToken: null,
  user: null,
  loginType: null,
  isWebGatewayEnabled: true,
}

export default {
  namespaced: true,

  /*!
   *
   * state
   *
   */
  state: clone(INITIAL_IDENTITY),

  /*
   *
   * getters
   *
   */
  getters: {
    hadIdentity: loadHadIdentity,
    isOwner: state => state.user?.access_level === ACCESS_LEVELS.OWNER,
    isManagerOrHigher: (state) => {
      return [
        ACCESS_LEVELS.OWNER,
        ACCESS_LEVELS.MANAGER,
      ].includes(state.user?.access_level)
    },
    userAccessLevel: state => state.user?.access_level,
    isReceptionOrHigher: (state) => {
      return [
        ACCESS_LEVELS.OWNER,
        ACCESS_LEVELS.MANAGER,
        ACCESS_LEVELS.RECEPTION,
      ].includes(state.user?.access_level)
    },
    isAdvancedOrHigher: (state) => {
      return [
        ACCESS_LEVELS.OWNER,
        ACCESS_LEVELS.MANAGER,
        ACCESS_LEVELS.RECEPTION,
        ACCESS_LEVELS.ADVANCED,
      ].includes(state.user?.access_level)
    },
  },

  /*
   *
   * actions
   *
   */
  actions: {
    login ({ commit, dispatch }, identity) {
      commit('setIdentity', identity)
      commit('setHadIdentity', true)

      // fallback if `business.current_staffer.staff_access_level` does not provide an `access_level` value even thought it should
      // this should be be overwritten by `business/loadBusiness` function
      if (identity.user?.access_level) {
        commit('updateStafferAccessLevel', identity.user?.access_level)
      }

      dispatch('saveIdentity')
    },

    logout ({ commit }) {
      commit('clearIdentity')
    },

    selectBusiness ({ commit, dispatch }, businessId) {
      commit('setBusinessId', businessId)
      dispatch('saveIdentity')
    },

    saveIdentity ({ rootState }) {
      const identity = omit(['isWebGatewayEnabled'], rootState.identity)
      saveIosTabletIdentity({ state: { ...rootState, identity } })
    },

    setStafferAccessLevel ({ dispatch, commit }, accessLevel) {
      commit('updateStafferAccessLevel', accessLevel)
      dispatch('saveIdentity')
    },

    reloadUser ({ commit, dispatch, state }) {
      return new Promise((resolve, reject) => {
        dispatch('api/call', {
          url: ENDPOINTS.ACCOUNT_ME,
          method: 'GET',
        }, { root: true }).then((response) => {
          commit('updateUser', response.data?.account)
          resolve(state.user)
        }).catch(reject)
      })
    },
  },

  /*
   *
   * mutations
   *
   */
  mutations: {
    setWebGatewayDisabled (state) {
      state.isWebGatewayEnabled = false
    },

    setIdentity (state, identity) {
      state.accessToken = identity.accessToken
      state.loginType = identity.loginType
      state.user = identity.user
      setWebGatewayIdentity(state)
    },

    updateStafferAccessLevel (state, accessLevel) {
      if (!accessLevel) throw new Error('no base access_level provided')

      state.user.access_level = accessLevel
      state.user.access_levels = createAccessLevels(accessLevel)
      setWebGatewayIdentity(state)
    },

    clearIdentity (state) {
      clearWebGatewayIdentity(state)
      Object.assign(state, {
        ...INITIAL_IDENTITY,
        isWebGatewayEnabled: state.isWebGatewayEnabled,
      })
      clearIosTabletIdentity()
    },

    setBusinessId (state, businessId) {
      state.user.businessId = businessId
      setWebGatewayIdentity(state)
    },

    setHadIdentity (state, hadIdentity) {
      saveHadIdentity(hadIdentity)
    },

    updateUser (state, user) {
      Object.assign(state.user, user)

      setWebGatewayIdentity(state)
    },
  },
}

const clearWebGatewayIdentity = (state) => {
  if (!state.isWebGatewayEnabled) return

  const { clearIdentity } = useWebGateway()
  clearIdentity()
}

const setWebGatewayIdentity = (state) => {
  if (!state.isWebGatewayEnabled) return

  const { setIdentity } = useWebGateway()
  setIdentity({
    accessToken: state.accessToken,
    loginType: state.loginType,
    profile: state.user,
  })
}

function createAccessLevels (accessLevel) {
  let accessLevels = {}

  accessLevels = ACCESS_LEVELS_ORDERED.reduce((levels, level) => {
    levels[level] = false
    return levels
  }, {})

  if (accessLevels) {
    ACCESS_LEVELS_ORDERED.slice(
      ACCESS_LEVELS_ORDERED.indexOf(accessLevel)
    ).map(level => {
      accessLevels[level] = true
    })
  }
  return accessLevels
}

function saveHadIdentity (hadIdentity) {
  localStorage.setItem(HAD_IDENTITY_KEY, hadIdentity)
}

function loadHadIdentity () {
  return localStorage.getItem(HAD_IDENTITY_KEY)
}
